import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { format, addMonths, subMonths, startOfMonth, getDaysInMonth, getDay, isSameDay, isToday, isBefore, startOfDay } from 'date-fns';
import { Sheet, SheetContent, SheetTitle, SheetClose } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  X, Loader2, Trash2, MapPin, ChevronLeft, ChevronRight, Clock,
  User, Stethoscope, Calendar, FileText, CheckCircle2, Sparkles,
  FlaskConical, History, Video, MessageSquare, Check, ArrowRight, ArrowLeft, Send,
} from 'lucide-react';
import SearchableSelect from '@/components/ui/searchable-select';
import { useToast } from '@/hooks/use-toast';
import {
  emrService,
  type EmrAppointment,
  type EmrLocation,
} from '@/services/emrService';
import { bookingService, type AppointmentType } from '@/services/bookingService';
import {
  intakeFormTemplateService,
  type IntakeFormTemplate,
} from '@/services/intakeFormTemplateService';
import { Switch } from '@/components/ui/switch';
import { patientService } from '@/services/patientService';
import { practitionerService } from '@/services/practitionerService';
import { transcriptService, type Transcript } from '@/services/transcriptService';
import type { Patient } from '@/lib/types';
import { useClinic } from '@/contexts/ClinicContext';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import TranscriptViewer from './TranscriptViewer';
import ConfirmDeleteDialog from '@/components/ui/confirm-delete-dialog';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ChevronDown } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import {
  VisitModeSelector,
  VideoCallSection,
  TranscriptSection,
  appointmentSchema,
  APPOINTMENT_TITLES,
  VISIT_MODES,
  getNextAvailableTimeSlot,
  isVideoVisit,
  type AppointmentFormData,
  type VisitMode,
} from './appointment';
import VisitTimeline from './VisitTimeline';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import PanelBrowserDialog from '@/components/labs/PanelBrowserDialog';
import { type LabPanel } from '@/services/labService';

// ========================================
// TYPES
// ========================================

interface WizardLabOrder {
  // Local row id, only used in the wizard for keying/removal.
  _localId: string;
  // Selected clinic-configured panel. One panel per order row.
  panel: LabPanel | null;
  // Mirrors the lab order request body. Derived from the panel when one is
  // picked; defaults to walk_in otherwise.
  collection_method: 'walk_in' | 'at_home';
  deadline_days_before: number;
  notes: string;
}

interface WizardData {
  // Step 1: Patient
  patient_id: string;
  patient?: { id: string; full_name: string; email?: string; phone?: string };
  // Step 2: Provider
  practitioner_id: string;
  practitioner?: { id: string; full_name: string };
  // Step 3: Appointment Type + Date + Slot
  appointment_type_id: string;
  appointmentType?: AppointmentType;
  date: string;
  start_time: string;
  duration: number;
  selectedSlot?: { start: string; end: string; local_start?: string; local_end?: string };
  // Step 4: Details
  location_id: string;
  status: string;
  title: string;
  notes: string;
  patient_notes: string;
  visit_mode: VisitMode;
  is_telehealth: boolean;
  // Step 4: Intake (optional)
  send_intake: boolean;
  intake_form_template_id: string | null;
  therapy_category: string | null;
  // Step 5: Pre-visit labs (optional; create-mode only)
  order_labs: boolean;
  lab_orders: WizardLabOrder[];
}

const INITIAL_DATA: WizardData = {
  patient_id: '',
  practitioner_id: '',
  appointment_type_id: '',
  date: '',
  start_time: '',
  duration: 30,
  location_id: '',
  status: 'scheduled',
  title: '',
  notes: '',
  patient_notes: '',
  visit_mode: 'in_person',
  is_telehealth: false,
  send_intake: false,
  intake_form_template_id: null,
  therapy_category: null,
  order_labs: false,
  lab_orders: [],
};

type WizardStep = 'patient' | 'provider' | 'schedule' | 'details' | 'labs' | 'review';

const ALL_STEPS: { key: WizardStep; label: string; icon: React.ElementType }[] = [
  { key: 'patient', label: 'Patient', icon: User },
  { key: 'provider', label: 'Provider', icon: Stethoscope },
  { key: 'schedule', label: 'Schedule', icon: Calendar },
  { key: 'details', label: 'Details', icon: FileText },
  { key: 'labs', label: 'Labs', icon: FlaskConical },
  { key: 'review', label: 'Review', icon: CheckCircle2 },
];

// The Labs step is only shown when creating a new appointment. In edit
// mode, lab management happens from the visit cockpit / AppointmentSheet.
const getStepsForMode = (isEditing: boolean) =>
  isEditing ? ALL_STEPS.filter(s => s.key !== 'labs') : ALL_STEPS;

// ========================================
// STEP INDICATOR
// ========================================

const StepIndicator: React.FC<{
  currentStep: WizardStep;
  onStepClick: (step: WizardStep) => void;
  completedSteps: Set<WizardStep>;
  steps: typeof ALL_STEPS;
}> = ({
  currentStep,
  onStepClick,
  completedSteps,
  steps,
}) => {
  const currentIndex = steps.findIndex(s => s.key === currentStep);
  return (
    <div className="flex items-center gap-1 px-6 py-3 border-b border-slate-200 bg-slate-50/50">
      {steps.map((step, idx) => {
        const isActive = step.key === currentStep;
        const isCompleted = completedSteps.has(step.key);
        const isClickable = isCompleted || idx <= currentIndex;
        const Icon = step.icon;
        return (
          <React.Fragment key={step.key}>
            {idx > 0 && (
              <div className={cn('flex-1 h-0.5 mx-1', idx <= currentIndex ? 'bg-brand-teal' : 'bg-slate-200')} />
            )}
            <button
              type="button"
              onClick={() => isClickable && onStepClick(step.key)}
              disabled={!isClickable}
              className={cn(
                'flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-medium transition-all',
                isActive && 'bg-brand-teal text-white shadow-sm',
                !isActive && isCompleted && 'bg-brand-teal/10 text-brand-teal cursor-pointer hover:bg-brand-teal/20',
                !isActive && !isCompleted && 'bg-slate-100 text-slate-400',
                isClickable && !isActive && 'cursor-pointer',
              )}
            >
              {isCompleted && !isActive ? (
                <Check className="h-3.5 w-3.5" />
              ) : (
                <Icon className="h-3.5 w-3.5" />
              )}
              <span className="hidden sm:inline">{step.label}</span>
            </button>
          </React.Fragment>
        );
      })}
    </div>
  );
};

// ========================================
// STEP 1: PATIENT SELECTION
// ========================================

const PatientStep: React.FC<{
  data: WizardData;
  onUpdate: (updates: Partial<WizardData>) => void;
  clinicId: string;
}> = ({ data, onUpdate, clinicId }) => {
  const [patients, setPatients] = useState<Patient[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  useEffect(() => {
    const loadInitial = async () => {
      try {
        const res = await patientService.getPatient({
          limit: 20,
          filter: { clinics: [clinicId], statuses: ['active'] },
        });
        setPatients(res?.data?.patients || res?.patients || res || []);
      } catch (err) {
        console.error('Error loading patients:', err);
      }
    };
    loadInitial();
  }, [clinicId]);

  const handleSearch = useCallback(async (query: string) => {
    if (!clinicId) return;
    setIsSearching(true);
    try {
      const res = await patientService.getPatient({
        limit: 1000,
        search: query,
        filter: { clinics: [clinicId], statuses: ['active'] },
      });
      setPatients(res?.data?.patients || res?.patients || res || []);
    } catch (err) {
      console.error('Error searching patients:', err);
    } finally {
      setIsSearching(false);
    }
  }, [clinicId]);

  const options = useMemo(
    () => patients.map(p => ({
      id: p.id,
      full_name: `${p.full_name}`,
      email: p.email || '',
      phone: p.phone || undefined,
    })),
    [patients]
  );

  const selected = useMemo(() => options.find(p => p.id === data.patient_id), [data.patient_id, options]);

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-base font-semibold text-slate-900">Select Patient</h3>
        <p className="text-sm text-slate-500 mt-0.5">Search for an existing patient to book an appointment.</p>
      </div>
      <div className="space-y-1.5">
        <Label className="required">Patient</Label>
        <SearchableSelect
          options={options}
          value={selected}
          onSelect={(opt) => onUpdate({
            patient_id: opt.id,
            patient: { id: opt.id, full_name: opt.full_name, email: opt.email, phone: opt.phone },
          })}
          onSearch={handleSearch}
          loading={isSearching}
          placeholder="Search for a patient..."
          searchPlaceholder="Type patient name or email..."
          type="patient"
        />
      </div>
      {data.patient && (
        <div className="rounded-lg border border-brand-teal/20 bg-brand-teal/5 p-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-brand-teal/10 flex items-center justify-center">
              <User className="h-5 w-5 text-brand-teal" />
            </div>
            <div>
              <p className="font-medium text-slate-900">{data.patient.full_name}</p>
              {data.patient.email && <p className="text-xs text-slate-500">{data.patient.email}</p>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ========================================
// STEP 2: PROVIDER SELECTION
// ========================================

const ProviderStep: React.FC<{
  data: WizardData;
  onUpdate: (updates: Partial<WizardData>) => void;
  clinicId: string;
}> = ({ data, onUpdate, clinicId }) => {
  const [practitioners, setPractitioners] = useState<{ id: string; full_name: string }[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  useEffect(() => {
    const loadPractitioners = async () => {
      try {
        const res = await practitionerService.getPractitionerByClinicId(clinicId, { limit: 20, status: 'active' });
        const pracs = res?.practitioners || res || [];
        setPractitioners(
          pracs.map((p: any) => ({
            id: p.id,
            full_name: `${p.first_name} ${p.last_name}${p.credentials ? `, ${p.credentials}` : ''}`,
          }))
        );
      } catch (err) {
        console.error('Error loading practitioners:', err);
      }
    };
    loadPractitioners();
  }, [clinicId]);

  const handleSearch = useCallback(async (query: string) => {
    if (!clinicId) return;
    setIsSearching(true);
    try {
      const res = await practitionerService.getPractitionerByClinicId(clinicId, { limit: 20, search: query, status: 'active' });
      const pracs = res?.practitioners || res || [];
      setPractitioners(
        pracs.map((p: any) => ({
          id: p.id,
          full_name: `${p.first_name} ${p.last_name}${p.credentials ? `, ${p.credentials}` : ''}`,
        }))
      );
    } catch (err) {
      console.error('Error searching practitioners:', err);
    } finally {
      setIsSearching(false);
    }
  }, [clinicId]);

  const options = useMemo(
    () => practitioners.map(p => ({ id: p.id, full_name: p.full_name, email: '', specialty: '' })),
    [practitioners]
  );

  const selected = useMemo(() => options.find(p => p.id === data.practitioner_id), [data.practitioner_id, options]);

  // Sync practitioner object when practitioner_id is pre-filled but practitioner object is missing
  useEffect(() => {
    if (data.practitioner_id && !data.practitioner && practitioners.length > 0) {
      const match = practitioners.find(p => p.id === data.practitioner_id);
      if (match) {
        onUpdate({ practitioner: { id: match.id, full_name: match.full_name } });
      }
    }
  }, [data.practitioner_id, data.practitioner, practitioners, onUpdate]);

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-base font-semibold text-slate-900">Select Provider</h3>
        <p className="text-sm text-slate-500 mt-0.5">Choose which practitioner will see the patient.</p>
      </div>
      <div className="space-y-1.5">
        <Label className="required">Provider</Label>
        <SearchableSelect
          options={options}
          value={selected}
          onSelect={(opt) => {
            onUpdate({
              practitioner_id: opt.id,
              practitioner: { id: opt.id, full_name: opt.full_name },
              // Reset schedule when provider changes
              date: '',
              start_time: '',
              selectedSlot: undefined,
              appointment_type_id: '',
              appointmentType: undefined,
            });
          }}
          onSearch={handleSearch}
          loading={isSearching}
          placeholder="Search for a provider..."
          searchPlaceholder="Type provider name..."
          type="practitioner"
        />
      </div>
      {data.practitioner && (
        <div className="rounded-lg border border-brand-teal/20 bg-brand-teal/5 p-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-brand-teal/10 flex items-center justify-center">
              <Stethoscope className="h-5 w-5 text-brand-teal" />
            </div>
            <div>
              <p className="font-medium text-slate-900">{data.practitioner.full_name}</p>
              <p className="text-xs text-slate-500">Provider</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ========================================
// STEP 3: SCHEDULE (Appointment Type + Date + Time)
// ========================================

const ScheduleStep: React.FC<{
  data: WizardData;
  onUpdate: (updates: Partial<WizardData>) => void;
  clinicId: string;
}> = ({ data, onUpdate, clinicId }) => {
  const [appointmentTypes, setAppointmentTypes] = useState<AppointmentType[]>([]);
  const [currentMonth, setCurrentMonth] = useState(() => {
    if (data.date) return startOfMonth(new Date(data.date + 'T00:00:00'));
    return startOfMonth(new Date());
  });
  const [availableDates, setAvailableDates] = useState<Map<string, boolean>>(new Map());
  const [isLoadingDates, setIsLoadingDates] = useState(false);
  const [slots, setSlots] = useState<Array<{ start: string; end: string; local_start?: string; local_end?: string }>>([]);
  const [isLoadingSlots, setIsLoadingSlots] = useState(false);

  // Load appointment types for the selected practitioner
  useEffect(() => {
    if (!clinicId || !data.practitioner_id) {
      setAppointmentTypes([]);
      return;
    }
    const loadAppointmentTypes = async () => {
      try {
        const types = await bookingService.getAppointmentTypes(clinicId, data.practitioner_id, true);
        setAppointmentTypes(types);
      } catch (err) {
        console.error('Error loading appointment types:', err);
      }
    };
    loadAppointmentTypes();
  }, [clinicId, data.practitioner_id]);

  // Load available dates for the month
  useEffect(() => {
    if (!data.practitioner_id) return;

    const loadDates = async () => {
      setIsLoadingDates(true);
      try {
        const year = currentMonth.getFullYear();
        const month = currentMonth.getMonth() + 1;
        const result = await emrService.getProviderAvailableDates(
          clinicId,
          data.practitioner_id,
          year,
          month,
        );
        const dateMap = new Map<string, boolean>();
        result.dates.forEach(d => dateMap.set(d.date, d.hasSlots));
        setAvailableDates(dateMap);
      } catch (err) {
        console.error('Error loading available dates:', err);
      } finally {
        setIsLoadingDates(false);
      }
    };
    loadDates();
  }, [clinicId, data.practitioner_id, currentMonth]);

  // Load slots for selected date
  useEffect(() => {
    if (!data.practitioner_id || !data.date) {
      setSlots([]);
      return;
    }

    const loadSlots = async () => {
      setIsLoadingSlots(true);
      try {
        const result = await emrService.getProviderAvailableSlots(
          clinicId,
          data.practitioner_id,
          data.date,
          undefined,
          data.appointment_type_id || undefined,
        );
        setSlots(result.slots || []);
        if (result.duration) {
          onUpdate({ duration: result.duration });
        }
      } catch (err) {
        console.error('Error loading slots:', err);
        setSlots([]);
      } finally {
        setIsLoadingSlots(false);
      }
    };
    loadSlots();
  }, [clinicId, data.practitioner_id, data.date, data.appointment_type_id]);

  // Calendar rendering helpers
  const daysInMonth = getDaysInMonth(currentMonth);
  const firstDayOfWeek = getDay(currentMonth);
  const today = startOfDay(new Date());

  const handleDateSelect = (day: number) => {
    const dateStr = format(new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day), 'yyyy-MM-dd');
    onUpdate({ date: dateStr, start_time: '', selectedSlot: undefined });
  };

  const handleSlotSelect = (slot: { start: string; end: string; local_start?: string; local_end?: string }) => {
    const slotDate = new Date(slot.start);
    const timeStr = format(slotDate, 'HH:mm');
    onUpdate({ start_time: timeStr, selectedSlot: slot });
  };

  const handleAppointmentTypeChange = (typeId: string) => {
    const at = appointmentTypes.find(t => t.id === typeId);
    onUpdate({
      appointment_type_id: typeId,
      appointmentType: at,
      duration: at?.duration_minutes || 30,
      title: at?.name || '',
      // Reset slot selection when appointment type changes
      start_time: '',
      selectedSlot: undefined,
    });
  };

  const activeAppointmentTypes = appointmentTypes.filter(t => t.is_active);

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-base font-semibold text-slate-900">Schedule Appointment</h3>
        <p className="text-sm text-slate-500 mt-0.5">{activeAppointmentTypes.length > 0 ? 'Pick an appointment type, then choose an available date and time.' : 'Choose an available date and time.'}</p>
      </div>

      {/* Appointment Type Selection — only shown if practitioner has types */}
      {activeAppointmentTypes.length > 0 && (
      <div className="space-y-1.5">
        <Label>Appointment Type</Label>
        <Select value={data.appointment_type_id} onValueChange={handleAppointmentTypeChange}>
          <SelectTrigger>
            <SelectValue placeholder="Select appointment type..." />
          </SelectTrigger>
          <SelectContent>
            {activeAppointmentTypes.map(t => (
              <SelectItem key={t.id} value={t.id}>
                <span className="flex items-center gap-2">
                  {t.color && <span className="w-2 h-2 rounded-full" style={{ backgroundColor: t.color }} />}
                  {t.name}
                  <span className="text-slate-400 text-xs">({t.duration_minutes} min)</span>
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      )}

      {/* Calendar */}
      <div className="rounded-lg border border-slate-200 overflow-hidden">
        {/* Month Navigation */}
        <div className="flex items-center justify-between px-4 py-2.5 bg-slate-50 border-b border-slate-200">
          <button
            type="button"
            onClick={() => setCurrentMonth(prev => subMonths(prev, 1))}
            className="p-1 rounded hover:bg-slate-200 transition-colors"
          >
            <ChevronLeft className="h-4 w-4 text-slate-600" />
          </button>
          <span className="text-sm font-semibold text-slate-800">
            {format(currentMonth, 'MMMM yyyy')}
          </span>
          <button
            type="button"
            onClick={() => setCurrentMonth(prev => addMonths(prev, 1))}
            className="p-1 rounded hover:bg-slate-200 transition-colors"
          >
            <ChevronRight className="h-4 w-4 text-slate-600" />
          </button>
        </div>

        {/* Day Headers */}
        <div className="grid grid-cols-7 border-b border-slate-100">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
            <div key={d} className="text-center text-[11px] font-medium text-slate-400 py-2">
              {d}
            </div>
          ))}
        </div>

        {/* Calendar Grid */}
        <div className="grid grid-cols-7 relative">
          {isLoadingDates && (
            <div className="absolute inset-0 bg-white/60 flex items-center justify-center z-10">
              <Loader2 className="h-5 w-5 animate-spin text-brand-teal" />
            </div>
          )}
          {/* Empty cells before first day */}
          {Array.from({ length: firstDayOfWeek }).map((_, i) => (
            <div key={`empty-${i}`} className="h-10" />
          ))}
          {/* Day cells */}
          {Array.from({ length: daysInMonth }).map((_, i) => {
            const day = i + 1;
            const dateStr = format(new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day), 'yyyy-MM-dd');
            const dateObj = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
            const isPast = isBefore(dateObj, today);
            const hasSlots = availableDates.get(dateStr);
            const isSelected = data.date === dateStr;
            const isTodayDate = isToday(dateObj);

            return (
              <button
                key={day}
                type="button"
                disabled={isPast || hasSlots === false}
                onClick={() => handleDateSelect(day)}
                className={cn(
                  'h-10 flex items-center justify-center text-sm transition-all relative',
                  isPast && 'text-slate-300 cursor-not-allowed',
                  !isPast && hasSlots && 'cursor-pointer hover:bg-brand-teal/10 text-slate-700 font-medium',
                  !isPast && hasSlots === false && 'text-slate-300 cursor-not-allowed',
                  !isPast && hasSlots === undefined && 'text-slate-500',
                  isSelected && 'bg-brand-teal text-white rounded-md hover:bg-brand-teal',
                  isTodayDate && !isSelected && 'font-bold',
                )}
              >
                {day}
                {hasSlots && !isSelected && (
                  <span className="absolute bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-brand-teal" />
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Time Slots */}
      {data.date && (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4 text-slate-500" />
            <Label className="text-sm font-medium">
              Available times for {format(new Date(data.date + 'T00:00:00'), 'EEEE, MMM d')}
            </Label>
          </div>

          {isLoadingSlots ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-brand-teal mr-2" />
              <span className="text-sm text-slate-500">Loading available slots...</span>
            </div>
          ) : slots.length === 0 ? (
            <div className="text-center py-6 bg-slate-50 rounded-lg border border-dashed border-slate-200">
              <Clock className="h-8 w-8 text-slate-300 mx-auto mb-2" />
              <p className="text-sm text-slate-500">No available slots for this date.</p>
              <p className="text-xs text-slate-400 mt-1">Try selecting a different date.</p>
            </div>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-[200px] overflow-y-auto pr-1">
              {slots.map((slot, idx) => {
                const slotTime = new Date(slot.start);
                const timeLabel = format(slotTime, 'h:mm a');
                const isSelected = data.selectedSlot?.start === slot.start;

                return (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => handleSlotSelect(slot)}
                    className={cn(
                      'px-3 py-2 rounded-md text-sm font-medium border transition-all',
                      isSelected
                        ? 'bg-brand-teal text-white border-brand-teal shadow-sm'
                        : 'bg-white text-slate-700 border-slate-200 hover:border-brand-teal hover:bg-brand-teal/5',
                    )}
                  >
                    {timeLabel}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

    </div>
  );
};

// ========================================
// STEP 4: DETAILS
// ========================================

const DetailsStep: React.FC<{
  data: WizardData;
  onUpdate: (updates: Partial<WizardData>) => void;
  clinicId: string;
  locations: EmrLocation[];
}> = ({ data, onUpdate, clinicId, locations }) => {
  const activeLocations = useMemo(() => locations.filter(l => l.is_active), [locations]);
  const singleLocation = activeLocations.length === 1 ? activeLocations[0] : null;
  const [intakeTemplates, setIntakeTemplates] = useState<IntakeFormTemplate[]>([]);

  // Auto-set single location
  useEffect(() => {
    if (singleLocation && !data.location_id) {
      onUpdate({ location_id: singleLocation.id });
    }
  }, [singleLocation, data.location_id]);

  // Load published intake form templates for the clinic
  useEffect(() => {
    if (!clinicId) return;
    intakeFormTemplateService
      .list(clinicId, { active: true, published: true })
      .then(setIntakeTemplates)
      .catch(() => setIntakeTemplates([]));
  }, [clinicId]);

  // Pull defaults from the chosen appointment type when it changes (only if
  // the user hasn't explicitly chosen something else).
  useEffect(() => {
    const at = data.appointmentType as any;
    if (!at) return;
    const tplId = at.default_intake_form_template_id || null;
    const therapy = at.default_therapy_category || null;
    if ((tplId || therapy) && !data.send_intake && !data.intake_form_template_id && !data.therapy_category) {
      onUpdate({
        send_intake: true,
        intake_form_template_id: tplId,
        therapy_category: therapy,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.appointmentType?.id]);

  const intakeSelectValue = data.intake_form_template_id
    ? `tpl:${data.intake_form_template_id}`
    : data.therapy_category
      ? `sys:${data.therapy_category}`
      : '';

  const handleIntakeSelect = (val: string) => {
    if (!val) {
      onUpdate({ intake_form_template_id: null, therapy_category: null });
      return;
    }
    if (val.startsWith('tpl:')) {
      onUpdate({ intake_form_template_id: val.slice(4), therapy_category: null });
    } else if (val.startsWith('sys:')) {
      onUpdate({ intake_form_template_id: null, therapy_category: val.slice(4) });
    }
  };

  const SYSTEM_INTAKES: { value: string; label: string }[] = [
    { value: 'sys:trt', label: 'TRT (Men’s Health) — system' },
    { value: 'sys:hrt', label: 'HRT (Women’s Health) — system' },
    { value: 'sys:glp1', label: 'GLP-1 (Weight Management) — system' },
    { value: 'sys:peptide', label: 'Peptide — system' },
    { value: 'sys:nad', label: 'NAD+ — system' },
    { value: 'sys:fertility', label: 'Fertility — system' },
    { value: 'sys:general', label: 'General — system' },
  ];

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-base font-semibold text-slate-900">Appointment Details</h3>
        <p className="text-sm text-slate-500 mt-0.5">Set visit mode, notes, and other details.</p>
      </div>

      {/* Visit Mode */}
      <VisitModeSelector
        value={data.visit_mode}
        onChange={(v) => onUpdate({ visit_mode: v, is_telehealth: v === 'video' || v === 'async_message' })}
      />

      {/* Location */}
      {singleLocation ? (
        <div className="space-y-1.5">
          <Label>Location</Label>
          <div className="flex h-9 items-center gap-2 rounded-md border border-gray-200 bg-gray-50 px-3 text-sm text-gray-600">
            <MapPin className="h-4 w-4 flex-shrink-0" />
            <span className="truncate">{singleLocation.name}</span>
          </div>
        </div>
      ) : activeLocations.length > 1 ? (
        <div className="space-y-1.5">
          <Label>Location</Label>
          <Select value={data.location_id} onValueChange={(v) => onUpdate({ location_id: v })}>
            <SelectTrigger>
              <SelectValue placeholder="Select location..." />
            </SelectTrigger>
            <SelectContent>
              {activeLocations.map(l => (
                <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      ) : null}

      {/* Status */}
      <div className="space-y-1.5">
        <Label className="required">Status</Label>
        <Select value={data.status} onValueChange={(v) => onUpdate({ status: v })}>
          <SelectTrigger>
            <SelectValue placeholder="Select status..." />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="scheduled">Scheduled</SelectItem>
            <SelectItem value="confirmed">Confirmed</SelectItem>
            <SelectItem value="checked_in">Checked In</SelectItem>
            <SelectItem value="in_progress">In Progress</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
            <SelectItem value="no_show">No-show</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Title */}
      <div className="space-y-1.5">
        <Label>Appointment Title</Label>
        <Select value={data.title || ''} onValueChange={(v) => onUpdate({ title: v })}>
          <SelectTrigger>
            <SelectValue placeholder="Select or type a title..." />
          </SelectTrigger>
          <SelectContent>
            {data.appointmentType && (
              <SelectItem value={data.appointmentType.name}>
                {data.appointmentType.name} (from appointment type)
              </SelectItem>
            )}
            {APPOINTMENT_TITLES.filter(t => t.value !== data.appointmentType?.name).map(t => (
              <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Pre-visit intake */}
      <div className="space-y-2 rounded-lg border border-slate-200 p-3">
        <div className="flex items-center justify-between gap-3">
          <div>
            <Label className="font-medium">Send pre-visit intake</Label>
            <p className="text-xs text-slate-500 mt-0.5">
              Email the patient an intake form they can complete before the visit.
            </p>
          </div>
          <Switch
            checked={data.send_intake}
            onCheckedChange={c => onUpdate({
              send_intake: c,
              ...(c ? {} : { intake_form_template_id: null, therapy_category: null }),
            })}
          />
        </div>

        {data.send_intake && (
          <div className="space-y-1.5">
            <Label>Form to send</Label>
            <Select value={intakeSelectValue} onValueChange={handleIntakeSelect}>
              <SelectTrigger>
                <SelectValue placeholder="Select an intake form..." />
              </SelectTrigger>
              <SelectContent>
                {intakeTemplates.length > 0 && (
                  <>
                    <div className="px-2 py-1 text-xs uppercase tracking-wide text-slate-400">
                      Custom
                    </div>
                    {intakeTemplates.map(t => (
                      <SelectItem key={t.id} value={`tpl:${t.id}`}>{t.name}</SelectItem>
                    ))}
                  </>
                )}
                <div className="px-2 py-1 text-xs uppercase tracking-wide text-slate-400 mt-1">
                  System
                </div>
                {SYSTEM_INTAKES.map(s => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {/* Notes */}
      <div className="space-y-1.5">
        <Label>Provider Notes</Label>
        <Textarea
          value={data.notes}
          onChange={(e) => onUpdate({ notes: e.target.value })}
          rows={2}
          maxLength={2000}
          placeholder="Internal notes for the provider..."
        />
      </div>
      <div className="space-y-1.5">
        <Label>Patient Instructions</Label>
        <Textarea
          value={data.patient_notes}
          onChange={(e) => onUpdate({ patient_notes: e.target.value })}
          rows={2}
          maxLength={1000}
          placeholder="Instructions or notes for the patient..."
        />
      </div>
    </div>
  );
};

// ========================================
// STEP 5: PRE-VISIT LABS (create-mode only)
// ========================================

const newLabRow = (): WizardLabOrder => ({
  _localId: `lab_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
  panel: null,
  collection_method: 'walk_in',
  deadline_days_before: 7,
  notes: '',
});

const inferCollectionMethod = (panel: LabPanel | null): 'walk_in' | 'at_home' => {
  const raw = panel?.collectionMethod || panel?.collection_method;
  return raw === 'at_home_phlebotomy' ? 'at_home' : 'walk_in';
};

const panelTestCount = (panel: LabPanel): number =>
  panel.markers?.length
  || panel.tests?.length
  || panel.internalTests?.length
  || 0;

const panelIsFasting = (panel: LabPanel): boolean =>
  panel.fastingRequired ?? panel.fasting_required ?? false;

const panelPrice = (panel: LabPanel): number | null => {
  const p = panel.patientPrice ?? panel.price;
  return typeof p === 'number' ? p : null;
};

const LabOrderRow: React.FC<{
  row: WizardLabOrder;
  index: number;
  showRemove: boolean;
  onEdit: () => void;
  onChange: (patch: Partial<WizardLabOrder>) => void;
  onRemove: () => void;
}> = ({ row, index, showRemove, onEdit, onChange, onRemove }) => {
  const [notesOpen, setNotesOpen] = useState(!!row.notes);
  const panel = row.panel;
  const hasPanel = !!panel;
  const tests = panel ? panelTestCount(panel) : 0;
  const fasting = panel ? panelIsFasting(panel) : false;
  const price = panel ? panelPrice(panel) : null;
  const turnaround = panel?.turnaroundDays;

  return (
    <div
      className={cn(
        'rounded-lg border bg-white p-3 space-y-3 transition',
        !hasPanel ? 'border-slate-200' : 'border-purple-100',
      )}
    >
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium uppercase tracking-wide text-slate-500">
          Lab order {index + 1}
        </span>
        {showRemove && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0 text-slate-400 hover:text-red-600"
            onClick={onRemove}
            aria-label="Remove lab order"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
      </div>

      {/* Panel — the hero choice. Empty state is a full-width call to action;
          selected state is a details strip with key panel facts inlined. */}
      {!hasPanel ? (
        <button
          type="button"
          onClick={onEdit}
          className="flex w-full items-center gap-3 rounded-md border border-dashed border-purple-300 bg-purple-50/30 px-3 py-3 text-left transition hover:border-purple-400 hover:bg-purple-50/60"
        >
          <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-md bg-purple-100 text-purple-700">
            <FlaskConical className="h-5 w-5" />
          </span>
          <span className="flex-1">
            <span className="block text-sm font-medium text-slate-900">Select a panel</span>
            <span className="block text-xs text-purple-700/80">
              Browse clinic-configured panels to choose what gets drawn
            </span>
          </span>
          <ArrowRight className="h-4 w-4 text-purple-400" />
        </button>
      ) : (
        <div className="flex items-start gap-3 rounded-md border border-purple-100 bg-purple-50/40 px-3 py-2.5">
          <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-md bg-purple-100 text-purple-700">
            <FlaskConical className="h-5 w-5" />
          </span>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium text-slate-900 truncate">{panel.name}</div>
            <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-slate-600">
              {tests > 0 && <span>{tests} {tests === 1 ? 'test' : 'tests'}</span>}
              {fasting && (
                <>
                  {tests > 0 && <span className="text-slate-300">·</span>}
                  <span className="text-amber-700">Fasting required</span>
                </>
              )}
              {typeof turnaround === 'number' && (
                <>
                  <span className="text-slate-300">·</span>
                  <span>{turnaround}d turnaround</span>
                </>
              )}
              {price != null && (
                <>
                  <span className="text-slate-300">·</span>
                  <span className="font-medium text-slate-700">${price.toFixed(2)}</span>
                </>
              )}
            </div>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 text-xs text-purple-700 hover:bg-purple-100"
            onClick={onEdit}
          >
            Change
          </Button>
        </div>
      )}

      {/* Compact options row. Defaults are sensible enough that most providers
          won't touch these — keep them visible but visually quieter than Panel.
          Stacks on narrow widths so the "days before" suffix never crowds the
          number input. */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="space-y-1">
          <Label className="text-xs text-slate-500">Collection</Label>
          <Select
            value={row.collection_method}
            onValueChange={(v: 'walk_in' | 'at_home') => onChange({ collection_method: v })}
          >
            <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="walk_in">Walk-in (lab center)</SelectItem>
              <SelectItem value="at_home">At-home phlebotomy</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-slate-500">Draw by</Label>
          <div className="relative">
            <Input
              type="number"
              min={1}
              max={60}
              className="h-9 pr-24 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
              value={Number.isFinite(row.deadline_days_before) ? row.deadline_days_before : ''}
              onChange={(e) => {
                const raw = e.target.value;
                onChange({
                  deadline_days_before: raw === '' ? NaN : parseInt(raw, 10),
                });
              }}
              onBlur={() => {
                const n = row.deadline_days_before;
                const clamped = Number.isFinite(n) ? Math.max(1, Math.min(60, n)) : 7;
                if (clamped !== n) {
                  onChange({ deadline_days_before: clamped });
                }
              }}
            />
            <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-xs text-slate-400">
              days before
            </span>
          </div>
        </div>
      </div>

      {/* Notes — collapsed by default since most orders don't need them. Stays
          open if the row already has notes (e.g., when re-editing). */}
      <Collapsible open={notesOpen} onOpenChange={setNotesOpen}>
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className="inline-flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-slate-700"
          >
            <ChevronDown
              className={cn(
                'h-3.5 w-3.5 transition-transform',
                notesOpen && 'rotate-180',
              )}
            />
            {notesOpen ? 'Hide notes' : 'Add notes (optional)'}
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent className="mt-2">
          <Textarea
            value={row.notes}
            onChange={(e) => onChange({ notes: e.target.value })}
            rows={2}
            maxLength={2000}
            placeholder="e.g. fasting required, special handling, prior history…"
          />
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
};

const LabsStep: React.FC<{
  data: WizardData;
  onUpdate: (updates: Partial<WizardData>) => void;
}> = ({ data, onUpdate }) => {
  const [editingRowId, setEditingRowId] = useState<string | null>(null);

  const updateRow = (id: string, patch: Partial<WizardLabOrder>) => {
    onUpdate({
      lab_orders: data.lab_orders.map(o => (o._localId === id ? { ...o, ...patch } : o)),
    });
  };

  const removeRow = (id: string) => {
    onUpdate({ lab_orders: data.lab_orders.filter(o => o._localId !== id) });
  };

  const addRow = () => {
    onUpdate({ lab_orders: [...data.lab_orders, newLabRow()] });
  };

  // When the toggle is turned off, also drop any rows so the user doesn't
  // accidentally submit hidden state. When turned on with no rows yet, seed one.
  const onToggleOrderLabs = (next: boolean) => {
    if (next && data.lab_orders.length === 0) {
      onUpdate({ order_labs: true, lab_orders: [newLabRow()] });
    } else if (!next) {
      onUpdate({ order_labs: false, lab_orders: [] });
    } else {
      onUpdate({ order_labs: true });
    }
  };

  const editingRow = data.lab_orders.find(o => o._localId === editingRowId) || null;

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between rounded-lg border border-purple-100 bg-purple-50/60 p-3">
        <div className="space-y-0.5">
          <div className="flex items-center gap-2 text-purple-700">
            <FlaskConical className="h-4 w-4" />
            <span className="font-medium">Order labs to be drawn before this visit?</span>
          </div>
          <p className="text-xs text-purple-700/80">
            Each order is a pre-visit lab. The patient receives a draw reminder
            before the appointment. Linked to this visit on creation.
          </p>
        </div>
        <Switch
          checked={data.order_labs}
          onCheckedChange={onToggleOrderLabs}
        />
      </div>

      {data.order_labs && (
        <div className="space-y-3">
          {data.lab_orders.map((row, idx) => (
            <LabOrderRow
              key={row._localId}
              row={row}
              index={idx}
              showRemove={data.lab_orders.length > 1}
              onEdit={() => setEditingRowId(row._localId)}
              onChange={(patch) => updateRow(row._localId, patch)}
              onRemove={() => removeRow(row._localId)}
            />
          ))}

          <button
            type="button"
            onClick={addRow}
            className="w-full rounded-lg border border-dashed border-slate-300 bg-white px-3 py-3 text-sm text-slate-600 transition hover:border-purple-300 hover:bg-purple-50/40 hover:text-purple-700"
          >
            <span className="inline-flex items-center gap-1.5">
              <FlaskConical className="h-4 w-4" />
              Add another lab order
            </span>
          </button>
        </div>
      )}

      {editingRow && (
        <PanelBrowserDialog
          open={!!editingRowId}
          onOpenChange={(o) => { if (!o) setEditingRowId(null); }}
          selectedPanelId={editingRow.panel?.id || null}
          onPanelSelected={(panel) => {
            updateRow(editingRow._localId, {
              panel,
              collection_method: inferCollectionMethod(panel),
            });
          }}
        />
      )}
    </div>
  );
};

// ========================================
// STEP 6: REVIEW
// ========================================

const ReviewStep: React.FC<{ data: WizardData; locations: EmrLocation[] }> = ({ data, locations }) => {
  const visitModeConfig = VISIT_MODES.find(m => m.value === data.visit_mode);
  const location = locations.find(l => l.id === data.location_id);

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-base font-semibold text-slate-900">Review Appointment</h3>
        <p className="text-sm text-slate-500 mt-0.5">Verify the details before creating the appointment.</p>
      </div>

      <div className="space-y-3 rounded-lg border border-slate-200 p-4">
        {/* Patient */}
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
            <User className="h-4 w-4 text-blue-600" />
          </div>
          <div>
            <p className="text-xs text-slate-500">Patient</p>
            <p className="font-medium text-slate-900">{data.patient?.full_name || '—'}</p>
          </div>
        </div>

        {/* Provider */}
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-lg bg-green-50 flex items-center justify-center flex-shrink-0">
            <Stethoscope className="h-4 w-4 text-green-600" />
          </div>
          <div>
            <p className="text-xs text-slate-500">Provider</p>
            <p className="font-medium text-slate-900">{data.practitioner?.full_name || '—'}</p>
          </div>
        </div>

        {/* Date & Time */}
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-lg bg-purple-50 flex items-center justify-center flex-shrink-0">
            <Calendar className="h-4 w-4 text-purple-600" />
          </div>
          <div>
            <p className="text-xs text-slate-500">Date & Time</p>
            <p className="font-medium text-slate-900">
              {data.date ? format(new Date(data.date + 'T00:00:00'), 'EEEE, MMMM d, yyyy') : '—'}
            </p>
            <p className="text-sm text-slate-600">
              {data.start_time || '—'} · {data.duration} minutes
            </p>
          </div>
        </div>

        {/* Appointment Type */}
        {data.appointmentType && (
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg bg-orange-50 flex items-center justify-center flex-shrink-0">
              <FileText className="h-4 w-4 text-orange-600" />
            </div>
            <div>
              <p className="text-xs text-slate-500">Appointment Type</p>
              <p className="font-medium text-slate-900">{data.appointmentType.name}</p>
            </div>
          </div>
        )}

        {/* Visit Mode */}
        <div className="flex items-start gap-3">
          <div className={cn(
            'w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0',
            data.visit_mode === 'in_person' ? 'bg-blue-50' : data.visit_mode === 'video' ? 'bg-green-50' : 'bg-purple-50'
          )}>
            {visitModeConfig && <visitModeConfig.icon className={cn(
              'h-4 w-4',
              data.visit_mode === 'in_person' ? 'text-blue-600' : data.visit_mode === 'video' ? 'text-green-600' : 'text-purple-600'
            )} />}
          </div>
          <div>
            <p className="text-xs text-slate-500">Visit Mode</p>
            <p className="font-medium text-slate-900">{visitModeConfig?.label || data.visit_mode}</p>
          </div>
        </div>

        {/* Location */}
        {location && (
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg bg-slate-50 flex items-center justify-center flex-shrink-0">
              <MapPin className="h-4 w-4 text-slate-600" />
            </div>
            <div>
              <p className="text-xs text-slate-500">Location</p>
              <p className="font-medium text-slate-900">{location.name}</p>
            </div>
          </div>
        )}

        {/* Notes */}
        {data.notes && (
          <div className="pt-2 border-t border-slate-100">
            <p className="text-xs text-slate-500 mb-1">Provider Notes</p>
            <p className="text-sm text-slate-700">{data.notes}</p>
          </div>
        )}
        {data.patient_notes && (
          <div className="pt-2 border-t border-slate-100">
            <p className="text-xs text-slate-500 mb-1">Patient Instructions</p>
            <p className="text-sm text-slate-700">{data.patient_notes}</p>
          </div>
        )}

        {/* Pre-visit lab orders */}
        {data.order_labs && data.lab_orders.length > 0 && (
          <div className="pt-2 border-t border-slate-100">
            <p className="text-xs text-slate-500 mb-1.5 flex items-center gap-1">
              <FlaskConical className="h-3.5 w-3.5" /> Pre-visit lab orders
            </p>
            <ul className="space-y-1">
              {data.lab_orders.map((o, i) => (
                <li key={o._localId} className="text-sm text-slate-700">
                  <span className="font-medium">Order {i + 1}:</span>{' '}
                  {o.panel
                    ? o.panel.name
                    : <span className="text-amber-600">no panel selected</span>}
                  <span className="text-slate-500"> · {o.collection_method === 'walk_in' ? 'walk-in' : 'at-home'} · draw {o.deadline_days_before}d before</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

// ========================================
// MESSAGING SECTION (for edit mode)
// ========================================

const MessagingSection: React.FC<{ patientId: string; appointmentId: string }> = ({ patientId, appointmentId }) => {
  const navigate = useNavigate();
  const { isAdmin } = useClinic();

  const handleStartMessaging = () => {
    if (isAdmin) {
      navigate(`/messaging?patientId=${patientId}&appointmentId=${appointmentId}`);
    } else {
      navigate(`/practitioner-portal/messaging?patientId=${patientId}&appointmentId=${appointmentId}`);
    }
  };

  return (
    <div className="space-y-2 rounded-lg border border-indigo-100 bg-indigo-50 p-3">
      <div className="flex items-center gap-2 text-indigo-700">
        <MessageSquare className="h-5 w-5" />
        <span className="font-medium">Async Messaging</span>
      </div>
      <p className="text-sm text-gray-600">
        This is an asynchronous messaging appointment. Communicate with the patient through secure messaging.
      </p>
      <Button type="button" variant="default" className="bg-indigo-600 hover:bg-indigo-700" onClick={handleStartMessaging}>
        <MessageSquare className="mr-2 h-4 w-4" />
        Start Messaging
      </Button>
    </div>
  );
};

// ========================================
// MAIN WIZARD COMPONENT
// ========================================

interface AppointmentWizardProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clinicId: string;
  appointment?: EmrAppointment | null;
  defaultDate?: string;
  defaultTime?: string;
  defaultPatientId?: string;
  onSave: () => void;
}

const AppointmentWizard: React.FC<AppointmentWizardProps> = ({
  open,
  onOpenChange,
  clinicId,
  appointment,
  defaultDate,
  defaultTime,
  defaultPatientId,
  onSave,
}) => {
  const { isPractitioner, isAdmin, isClinicStaff } = useClinic();
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  const isEditing = !!appointment;
  const [currentStep, setCurrentStep] = useState<WizardStep>('patient');
  const [completedSteps, setCompletedSteps] = useState<Set<WizardStep>>(new Set());
  const [wizardData, setWizardData] = useState<WizardData>({ ...INITIAL_DATA });
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingAppointment, setIsLoadingAppointment] = useState(false);
  const [fetchedAppointment, setFetchedAppointment] = useState<EmrAppointment | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isResendingLink, setIsResendingLink] = useState(false);
  const [transcript, setTranscript] = useState<Transcript | null>(null);
  const [isLoadingTranscript, setIsLoadingTranscript] = useState(false);
  const [showTranscriptViewer, setShowTranscriptViewer] = useState(false);
  const [locations, setLocations] = useState<EmrLocation[]>([]);

  const activeAppointment = fetchedAppointment || appointment;

  const userRole = useMemo(() => {
    if (isAdmin) return 'admin';
    if (isPractitioner) return 'practitioner';
    return 'clinic_staff';
  }, [isAdmin, isPractitioner, false]);

  // When the logged-in user is a practitioner booking for themselves, skip the Provider step.
  // Practitioner staff (who may book on behalf of a different practitioner) still see it.
  const shouldSkipProvider = isPractitioner && !!user?.practitionerId;

  // When opened from a patient page (defaultPatientId is set) for a NEW appointment,
  // the patient is already known — skip the Patient selection step.
  const shouldSkipPatient = !appointment?.id && !!defaultPatientId;

  const steps = useMemo(
    () =>
      getStepsForMode(isEditing).filter(s => {
        if (s.key === 'provider' && shouldSkipProvider) return false;
        if (s.key === 'patient' && shouldSkipPatient) return false;
        return true;
      }),
    [shouldSkipProvider, shouldSkipPatient, isEditing],
  );

  // Load locations
  useEffect(() => {
    if (!open || !clinicId) return;
    const loadData = async () => {
      try {
        const locRes = await emrService.getLocations(clinicId);
        setLocations(locRes);
      } catch (err) {
        console.error('Error loading data:', err);
      }
    };
    loadData();
  }, [open, clinicId]);

  // Fetch appointment details when editing
  useEffect(() => {
    if (!open || !clinicId || !appointment?.id) {
      setFetchedAppointment(null);
      return;
    }
    const fetchDetails = async () => {
      setIsLoadingAppointment(true);
      try {
        const data = await emrService.getAppointment(clinicId, appointment.id);
        setFetchedAppointment(data);
      } catch (err) {
        console.error('Error fetching appointment:', err);
        setFetchedAppointment(null);
      } finally {
        setIsLoadingAppointment(false);
      }
    };
    fetchDetails();
  }, [open, clinicId, appointment?.id]);

  // Initialize wizard data
  useEffect(() => {
    if (!open) return;

    if (isEditing && activeAppointment && !isLoadingAppointment) {
      const visitMode = (activeAppointment as any).visit_mode || (activeAppointment.is_telehealth ? 'video' : 'in_person');
      setWizardData({
        patient_id: activeAppointment.patient_id,
        patient: activeAppointment.patient
          ? { id: activeAppointment.patient.id, full_name: `${activeAppointment.patient.first_name} ${activeAppointment.patient.last_name}`, email: '', phone: activeAppointment.patient.phone || '' }
          : activeAppointment.patient_name ? { id: activeAppointment.patient_id, full_name: activeAppointment.patient_name } : undefined,
        practitioner_id: activeAppointment.practitioner_id,
        practitioner: activeAppointment.practitioner
          ? { id: activeAppointment.practitioner.id, full_name: `${activeAppointment.practitioner.first_name} ${activeAppointment.practitioner.last_name}` }
          : activeAppointment.practitioner_name ? { id: activeAppointment.practitioner_id, full_name: activeAppointment.practitioner_name } : undefined,
        appointment_type_id: '',
        appointmentType: undefined,
        date: format(new Date(activeAppointment.start_time), 'yyyy-MM-dd'),
        start_time: format(new Date(activeAppointment.start_time), 'HH:mm'),
        duration: activeAppointment.duration,
        location_id: activeAppointment.location_id || '',
        status: activeAppointment.status,
        title: activeAppointment.title || '',
        notes: activeAppointment.notes || '',
        patient_notes: activeAppointment.patient_notes || '',
        visit_mode: visitMode as VisitMode,
        is_telehealth: activeAppointment.is_telehealth || false,
      });
      // When editing, all steps are considered "completed" so user can navigate freely
      setCompletedSteps(new Set(['patient', 'provider', 'schedule', 'details', 'review']));
      setCurrentStep('review');
    } else if (!isEditing) {
      const smartDefaults = getNextAvailableTimeSlot();
      setWizardData({
        ...INITIAL_DATA,
        patient_id: defaultPatientId || '',
        practitioner_id: user?.practitionerId || '',
        date: defaultDate || '',
        start_time: defaultTime || '',
      });
      const preCompleted = new Set<WizardStep>();
      if (shouldSkipProvider) preCompleted.add('provider');
      if (shouldSkipPatient) preCompleted.add('patient');
      setCompletedSteps(preCompleted);
      setCurrentStep(steps[0]?.key ?? 'patient');
    }
  }, [open, isEditing, activeAppointment, isLoadingAppointment, defaultDate, defaultTime, defaultPatientId, user, shouldSkipProvider, shouldSkipPatient, steps]);

  // Fetch patient name when patient_id is set but patient object is missing
  useEffect(() => {
    if (!open || !wizardData.patient_id || wizardData.patient) return;

    const fetchPatient = async () => {
      try {
        const res: any = await patientService.getPatientById(wizardData.patient_id);
        const p = res?.data?.patient || res?.patient || res?.data || res;
        if (p?.full_name || p?.first_name || p?.last_name) {
          setWizardData(prev => ({
            ...prev,
            patient: {
              id: prev.patient_id,
              full_name: p.full_name || `${p.first_name || ''} ${p.last_name || ''}`.trim(),
              email: p.email || '',
              phone: p.phone || '',
            },
          }));
        }
      } catch (err) {
        console.error('Error fetching patient for review:', err);
      }
    };
    fetchPatient();
  }, [open, wizardData.patient_id, wizardData.patient]);

  // Fetch practitioner name when practitioner_id is set but practitioner object is missing
  useEffect(() => {
    if (!open || !clinicId || !wizardData.practitioner_id || wizardData.practitioner) return;

    const fetchPractitioner = async () => {
      try {
        const res: any = await practitionerService.getPractitionerById(clinicId, wizardData.practitioner_id);
        const p = res?.data?.practitioner || res?.practitioner || res;
        if (p?.first_name || p?.last_name) {
          setWizardData(prev => ({
            ...prev,
            practitioner: {
              id: prev.practitioner_id,
              full_name: `${p.first_name} ${p.last_name}${p.credentials ? `, ${p.credentials}` : ''}`,
            },
          }));
        }
      } catch (err) {
        console.error('Error fetching practitioner for review:', err);
      }
    };
    fetchPractitioner();
  }, [open, clinicId, wizardData.practitioner_id, wizardData.practitioner]);

  // Load transcript for edit mode
  useEffect(() => {
    if (!activeAppointment?.id || !isVideoVisit(activeAppointment) || !['completed', 'in_progress'].includes(activeAppointment.status)) {
      setTranscript(null);
      return;
    }
    const fetchTranscript = async () => {
      setIsLoadingTranscript(true);
      try {
        setTranscript(await transcriptService.getAppointmentTranscript(clinicId, activeAppointment.id));
      } catch {
        setTranscript(null);
      } finally {
        setIsLoadingTranscript(false);
      }
    };
    if (open && !isLoadingAppointment) fetchTranscript();
  }, [open, activeAppointment?.id, activeAppointment?.is_telehealth, activeAppointment?.status, clinicId, isLoadingAppointment]);

  const updateWizardData = useCallback((updates: Partial<WizardData>) => {
    setWizardData(prev => ({ ...prev, ...updates }));
  }, []);

  // Step validation
  const isStepValid = useCallback((step: WizardStep): boolean => {
    switch (step) {
      case 'patient': return !!wizardData.patient_id;
      case 'provider': return !!wizardData.practitioner_id;
      case 'schedule': return !!wizardData.date && !!wizardData.start_time && wizardData.duration >= 15;
      case 'details': return !!wizardData.status;
      case 'labs':
        // Labs are optional — toggle off is always fine.
        if (!wizardData.order_labs) return true;
        // Toggle on means the user committed to ordering at least one lab.
        // `[].every(...)` is vacuously true, which previously let an empty
        // lab_orders array slip through and silently drop the labs at
        // submit. Require at least one row AND every row to have a panel.
        return (
          wizardData.lab_orders.length > 0
          && wizardData.lab_orders.every(o => !!o.panel)
        );
      case 'review': return true;
      default: return false;
    }
  }, [wizardData]);

  const goToNextStep = useCallback(() => {
    const stepIndex = steps.findIndex(s => s.key === currentStep);
    if (isStepValid(currentStep)) {
      setCompletedSteps(prev => new Set([...prev, currentStep]));
      if (stepIndex < steps.length - 1) {
        setCurrentStep(steps[stepIndex + 1].key);
      }
    }
  }, [currentStep, isStepValid, steps]);

  const goToPrevStep = useCallback(() => {
    const stepIndex = steps.findIndex(s => s.key === currentStep);
    if (stepIndex > 0) {
      setCurrentStep(steps[stepIndex - 1].key);
    }
  }, [currentStep, steps]);

  const handleStepClick = useCallback((step: WizardStep) => {
    // Mark current step as completed if valid
    if (isStepValid(currentStep)) {
      setCompletedSteps(prev => new Set([...prev, currentStep]));
    }
    setCurrentStep(step);
  }, [currentStep, isStepValid]);

  const handleSubmit = useCallback(async () => {
    setIsLoading(true);
    try {
      const start = new Date(`${wizardData.date}T${wizardData.start_time}:00`);
      const isTelehealth = wizardData.visit_mode === 'video' || wizardData.visit_mode === 'async_message';

      const payload: any = {
        patient_id: wizardData.patient_id,
        practitioner_id: wizardData.practitioner_id,
        appointment_type_id: wizardData.appointment_type_id || undefined,
        location_id: wizardData.location_id || undefined,
        appointment_type_id: wizardData.appointment_type_id || undefined,
        start_time: start.toISOString(),
        duration: wizardData.duration,
        status: wizardData.status,
        title: wizardData.title,
        notes: wizardData.notes,
        patient_notes: wizardData.patient_notes,
        visit_mode: wizardData.visit_mode,
        is_telehealth: isTelehealth,
      };

      // Pre-visit intake (only on create — updates leave intake state untouched)
      if (!isEditing && wizardData.send_intake) {
        payload.send_intake = true;
        if (wizardData.intake_form_template_id) {
          payload.intake_form_template_id = wizardData.intake_form_template_id;
        } else if (wizardData.therapy_category) {
          payload.therapy_category = wizardData.therapy_category;
        }
      }

      // Pre-visit labs (create only). The labs step's Next/Create button
      // already requires every row to have a panel, but guard here too so
      // a state-shape bug (e.g. wizard reset mid-flow) can't silently
      // drop the user's labs and surface a misleading success toast.
      if (!isEditing && wizardData.order_labs) {
        const validRows = wizardData.lab_orders.filter(o => !!o.panel);
        if (validRows.length === 0) {
          toast({
            title: 'Lab orders incomplete',
            description:
              'You enabled pre-visit labs but no panel is selected. Pick a panel for each row or turn the toggle off.',
            variant: 'destructive',
          });
          setIsLoading(false);
          setCurrentStep('labs');
          return;
        }
        payload.lab_orders = validRows.map(row => ({
          panel_id: row.panel!.id,
          panel_name: row.panel!.name,
          collection_method: row.collection_method === 'at_home' ? 'at_home_phlebotomy' : 'walk_in',
          deadline_days_before: row.deadline_days_before,
          notes: row.notes || undefined,
        }));
      }

      if (isEditing && appointment) {
        await emrService.updateAppointment(clinicId, appointment.id, payload);
        toast({ title: 'Appointment Updated', description: 'The appointment has been updated successfully.' });
      } else {
        const created: any = await emrService.createAppointment(clinicId, payload);
        const intakeSent = created?.intake?.success;
        const labResults: Array<{ success: boolean; orderId?: string; error?: string }> = created?.lab_orders || [];
        const labSucceeded = labResults.filter(r => r.success).length;
        const labFailed = labResults.filter(r => !r.success);

        const messages: string[] = ['Appointment booked.'];
        if (intakeSent) messages.push('Intake link sent.');
        if (labSucceeded > 0) messages.push(`${labSucceeded} lab order${labSucceeded === 1 ? '' : 's'} created.`);

        if (labFailed.length > 0) {
          // Surface partial failure as a single non-blocking warning toast.
          toast({
            title: 'Appointment booked — some lab orders failed',
            description: `${labFailed.length} lab order${labFailed.length === 1 ? '' : 's'} could not be created: ${labFailed.map(r => r.error).filter(Boolean).slice(0, 2).join('; ')}${labFailed.length > 2 ? '…' : ''}`,
            variant: 'destructive',
          });
        } else {
          toast({
            title: 'Appointment Created',
            description: messages.join(' '),
          });
        }
      }

      onSave();
      onOpenChange(false);
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to save appointment. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  }, [wizardData, isEditing, appointment, clinicId, toast, onSave, onOpenChange]);

  const handleDeleteConfirm = useCallback(async () => {
    if (!appointment || !clinicId) throw new Error('Missing appointment or clinic ID');
    await emrService.deleteAppointment(clinicId, appointment.id);
    onSave();
    onOpenChange(false);
  }, [appointment, clinicId, onSave, onOpenChange]);

  const handleResendLink = useCallback(async () => {
    if (!appointment || !clinicId) return;
    setIsResendingLink(true);
    try {
      await emrService.resendAppointmentLink(clinicId, appointment.id);
      toast({
        title: 'Link sent',
        description: 'Appointment link resent to patient successfully',
        variant: 'success',
      });
    } catch (error: any) {
      toast({
        title: 'Failed to resend',
        description: error?.response?.data?.error?.message || 'Could not resend appointment link',
        variant: 'destructive',
      });
    } finally {
      setIsResendingLink(false);
    }
  }, [appointment, clinicId, toast]);

  const currentStepIndex = steps.findIndex(s => s.key === currentStep);
  const isLastStep = currentStepIndex === steps.length - 1;
  const isFirstStep = currentStepIndex === 0;

  // Video/transcript section visibility
  const currentVisitMode = wizardData.visit_mode;
  const showVideoSection =
    isEditing && !isLoadingAppointment && activeAppointment &&
    currentVisitMode === 'video' && isVideoVisit(activeAppointment) &&
    activeAppointment.telehealth_link && activeAppointment.status !== 'cancelled' &&
    (isAdmin || isClinicStaff || user?.practitionerId === activeAppointment.practitioner_id);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="flex h-full flex-col sm:max-w-[560px] p-0">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <SheetTitle className="text-base font-semibold">
            {isEditing ? 'Edit Appointment' : 'New Appointment'}
          </SheetTitle>
          <SheetClose asChild>
            <button className="flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 hover:bg-slate-50 transition-colors">
              <X className="h-4 w-4 text-slate-600" />
            </button>
          </SheetClose>
        </div>

        {/* Step Indicator */}
        <StepIndicator
          currentStep={currentStep}
          onStepClick={handleStepClick}
          completedSteps={completedSteps}
          steps={steps}
        />

        {/* Loading State */}
        {isEditing && isLoadingAppointment ? (
          <div className="flex flex-1 items-center justify-center">
            <div className="flex flex-col items-center gap-3">
              <Loader2 className="h-8 w-8 animate-spin text-brand-teal" />
              <p className="text-sm text-gray-500">Loading appointment details...</p>
            </div>
          </div>
        ) : (
          <>
            {/* Step Content */}
            <div className="flex-1 overflow-y-auto px-6 py-4">
              {currentStep === 'patient' && (
                <PatientStep data={wizardData} onUpdate={updateWizardData} clinicId={clinicId} />
              )}
              {currentStep === 'provider' && (
                <ProviderStep data={wizardData} onUpdate={updateWizardData} clinicId={clinicId} />
              )}
              {currentStep === 'schedule' && (
                <ScheduleStep data={wizardData} onUpdate={updateWizardData} clinicId={clinicId} />
              )}
              {currentStep === 'details' && (
                <DetailsStep
                  data={wizardData}
                  onUpdate={updateWizardData}
                  clinicId={clinicId}
                  locations={locations}
                />
              )}
              {currentStep === 'labs' && (
                <LabsStep data={wizardData} onUpdate={updateWizardData} />
              )}
              {currentStep === 'review' && (
                <>
                  <ReviewStep data={wizardData} locations={locations} />

                  {/* Edit-mode sections */}
                  {isEditing && activeAppointment && (
                    <div className="mt-4 space-y-3">
                      {/* Pre-Appointment Labs */}
                      <div className="space-y-2 rounded-lg border border-purple-100 bg-purple-50 p-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 text-purple-700">
                            <FlaskConical className="h-5 w-5" />
                            <span className="font-medium">Pre-Appointment Labs</span>
                          </div>
                          <Button type="button" variant="outline" size="sm" onClick={() => {
                            toast({ title: 'Feature Coming Soon', description: 'Pre-appointment lab ordering will be available soon' });
                          }}>
                            Request Lab Test
                          </Button>
                        </div>
                        <p className="text-sm text-purple-600">
                          Request lab tests to be completed before this appointment.
                        </p>
                      </div>

                      {/* Video Call Section */}
                      {showVideoSection && (
                        <VideoCallSection
                          appointmentId={activeAppointment.id}
                          telehealthLink={activeAppointment.telehealth_link!}
                          userRole={userRole}
                          showJoinButton={isPractitioner}
                        />
                      )}

                      {/* Async Messaging Section */}
                      {wizardData.visit_mode === 'async_message' && activeAppointment.status !== 'cancelled' && (
                        <MessagingSection patientId={activeAppointment.patient_id} appointmentId={activeAppointment.id} />
                      )}

                      {/* Transcript Section */}
                      {(activeAppointment.is_telehealth || (activeAppointment as any)?.visit_mode === 'video') &&
                        ['completed', 'in_progress'].includes(activeAppointment.status) && (
                        <div className="space-y-2 rounded-lg border border-blue-100 bg-blue-50 p-3">
                          <div className="flex items-center gap-2 text-blue-700">
                            <FileText className="h-5 w-5" />
                            <span className="font-medium">Call Transcript</span>
                          </div>
                          {isLoadingTranscript ? (
                            <div className="flex items-center gap-2 text-sm text-gray-500">
                              <Loader2 className="h-4 w-4 animate-spin" />
                              Checking for transcript...
                            </div>
                          ) : transcript ? (
                            <div className="space-y-2">
                              <div className="flex items-center gap-2 text-sm text-emerald-600">
                                <FileText className="h-4 w-4" />
                                <span>Transcript available ({transcript.word_count || 0} words){transcript.ai_summary && ' · AI Summary ready'}</span>
                              </div>
                              <div className="flex gap-2">
                                <Button type="button" variant="outline" size="sm" onClick={() => setShowTranscriptViewer(true)}>
                                  <FileText className="mr-2 h-4 w-4" />View Transcript
                                </Button>
                                {transcript.status === 'completed' && !transcript.soap_note_generated && (
                                  <Button type="button" variant="outline" size="sm" className="border-purple-200 text-purple-600 hover:bg-purple-50" onClick={() => setShowTranscriptViewer(true)}>
                                    <Sparkles className="mr-2 h-4 w-4" />Generate SOAP Note
                                  </Button>
                                )}
                              </div>
                            </div>
                          ) : (
                            <p className="text-sm text-gray-500">
                              {activeAppointment.status === 'completed'
                                ? 'Transcript processing... This may take a few minutes.'
                                : 'Transcript will be available after the call.'}
                            </p>
                          )}
                        </div>
                      )}

                      {/* Visit Timeline */}
                      {['completed', 'in_progress'].includes(activeAppointment.status) && (
                        <Collapsible>
                          <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                            <CollapsibleTrigger className="flex w-full items-center justify-between">
                              <div className="flex items-center gap-2 text-gray-700">
                                <History className="h-5 w-5" />
                                <span className="font-medium">Visit Timeline</span>
                              </div>
                              <ChevronDown className="h-4 w-4 text-gray-500" />
                            </CollapsibleTrigger>
                            <CollapsibleContent className="pt-4">
                              <VisitTimeline
                                appointmentId={activeAppointment.id}
                                clinicId={clinicId}
                                compact
                                limit={10}
                                showHeader={false}
                              />
                            </CollapsibleContent>
                          </div>
                        </Collapsible>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Footer Actions */}
            <div className="flex flex-shrink-0 items-center justify-between border-t border-slate-200 bg-white px-6 py-4">
              <div className="flex items-center gap-2">
                {isEditing && activeAppointment?.status === 'cancelled' ? (
                  <div className="inline-flex items-center gap-2 rounded-full bg-red-100 px-3 py-1.5 text-sm font-medium text-red-700">
                    <X className="h-4 w-4" />
                    Cancelled
                  </div>
                ) : isEditing && !isLoadingAppointment ? (
                  <>
                    <Button type="button" variant="destructive" size="sm" onClick={() => setShowDeleteDialog(true)}>
                      <Trash2 className="mr-2 h-4 w-4" />
                      Cancel Appointment
                    </Button>
                    {!['completed', 'no_show'].includes(activeAppointment?.status || '') && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleResendLink}
                        disabled={isResendingLink}
                        title="Resend appointment link to patient"
                      >
                        {isResendingLink ? (
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : (
                          <Send className="mr-2 h-4 w-4" />
                        )}
                        Resend Link
                      </Button>
                    )}
                  </>
                ) : !isFirstStep ? (
                  <Button type="button" variant="outline" size="sm" onClick={goToPrevStep}>
                    <ArrowLeft className="mr-1 h-4 w-4" />
                    Back
                  </Button>
                ) : null}
              </div>

              <div className="flex gap-2">
                {isFirstStep && !isEditing && (
                  <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                    Cancel
                  </Button>
                )}

                {activeAppointment?.status !== 'cancelled' && (
                  <>
                    {isLastStep ? (
                      <Button
                        type="button"
                        onClick={handleSubmit}
                        disabled={
                          isLoading
                          || !isStepValid('patient')
                          || !isStepValid('provider')
                          || !isStepValid('schedule')
                          || !isStepValid('details')
                          || (!isEditing && !isStepValid('labs'))
                        }
                      >
                        {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        {isEditing ? 'Update' : 'Create Appointment'}
                      </Button>
                    ) : (
                      <Button
                        type="button"
                        onClick={goToNextStep}
                        disabled={!isStepValid(currentStep)}
                      >
                        Next
                        <ArrowRight className="ml-1 h-4 w-4" />
                      </Button>
                    )}
                  </>
                )}
              </div>
            </div>
          </>
        )}
      </SheetContent>

      {/* Transcript Viewer Dialog */}
      <Dialog open={showTranscriptViewer} onOpenChange={setShowTranscriptViewer}>
        <DialogContent className="h-[80vh] max-w-4xl overflow-hidden p-0">
          {transcript && (
            <TranscriptViewer
              transcript={transcript}
              clinicId={clinicId}
              onNoteGenerated={() => {
                transcriptService.getTranscript(clinicId, transcript.id).then(setTranscript).catch(console.error);
              }}
              onClose={() => setShowTranscriptViewer(false)}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Cancel Confirmation Dialog */}
      <ConfirmDeleteDialog
        open={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
        title="Cancel Appointment"
        description="Are you sure you want to cancel this appointment? The appointment will be marked as cancelled and any scheduled reminders will be cancelled."
        itemType="appointment"
        onConfirm={handleDeleteConfirm}
        actionText="Cancel Appointment"
        successMessage="Appointment cancelled successfully"
      />
    </Sheet>
  );
};

export default AppointmentWizard;
